'use client';

import { Key, ReactNode, useEffect, useRef, useState } from 'react';
import { ComboBox } from 'react-aria-components';

import CloseIcon from '@/fo/icons/base/icons/close24.svg';
import { UiKitButtonBase } from '@/fo/ui/kit/button/base';
import { UiKitInputSearch } from '@/fo/ui/kit/input/search';
import { Option, UiKitListboxBase } from '@/fo/ui/kit/listbox/base';

import { styles } from './UiKitAutocompleteBase.styles';

export interface UiKitAutocompleteBaseProps<T extends Option> {
  options: T[];
  disabled?: boolean;
  label?: string;
  placeholder?: string;
  helperText?: string;
  onChange?: (value: string) => void;
  value?: string;
  errorMessage?: string;
  onSelect?: (key: Key | null) => void;
  startAdornment?: ReactNode;
  endAdornment?: ReactNode;
  renderOption?: (option: T) => ReactNode;
  className?: string;
  name?: string;
  classNameInput?: string;
  required?: boolean;
  error?: boolean;
  allowCustomValue?: boolean;
  menuTrigger?: 'focus' | 'input' | 'manual';
  notResetValue?: boolean;
  onBlur?: () => void;
}

export function UiKitAutocompleteBase<T extends Option>({
  options,
  disabled,
  onChange,
  value,
  helperText,
  errorMessage,
  placeholder,
  label,
  onSelect,
  startAdornment,
  endAdornment,
  renderOption,
  className,
  name,
  classNameInput,
  required,
  error,
  allowCustomValue = true,
  menuTrigger = 'focus',
  notResetValue = false,
  onBlur
}: UiKitAutocompleteBaseProps<T>) {
  const [inputValue, setInputValue] = useState(value ?? '');

  useEffect(() => {
    setInputValue(value ?? '');
  }, [value]);

  const slots = styles({ empty: inputValue === '' });

  const triggerRef = useRef<HTMLDivElement>(null);

  const handleClear = () => {
    onChange?.('');
    setInputValue('');
  };

  const handleChange = (newValue: string) => {
    // это нужно для того, чтобы при отправке формы не сбрасывалось значение
    const isFocused = triggerRef.current?.contains(document.activeElement);

    if (!isFocused && newValue === '' && notResetValue) {
      return;
    }

    setInputValue(newValue);
    onChange?.(newValue);
  };

  return (
    <div className={slots.base({ className })}>
      <ComboBox
        onInputChange={handleChange}
        inputValue={inputValue}
        allowsCustomValue={allowCustomValue}
        isDisabled={disabled}
        isInvalid={!!errorMessage}
        menuTrigger={menuTrigger}
        className={slots.menu()}
        onSelectionChange={onSelect}
        defaultFilter={() => true}
        allowsEmptyCollection
      >
        <UiKitInputSearch
          ref={triggerRef}
          helperText={helperText}
          errorMessage={errorMessage}
          placeholder={placeholder}
          label={label}
          startAdornment={startAdornment}
          name={name}
          className={slots.input({ className: classNameInput })}
          error={error}
          endAdornment={
            <>
              {endAdornment}
              <UiKitButtonBase
                color="ghost"
                rounded="full"
                className={slots.button()}
                onClick={handleClear}
              >
                <CloseIcon className={slots.closeIcon()} />
              </UiKitButtonBase>
            </>
          }
          required={required}
          onBlur={onBlur}
        />
        <UiKitListboxBase
          options={options}
          renderOption={renderOption}
          triggerRef={triggerRef}
        />
      </ComboBox>
    </div>
  );
}
